import { lazy, type ComponentType } from "react"

// Stale-deploy recovery for code-split chunks.
//
// After a new build is deployed, a client still running the previous index.html
// asks for hashed chunk files (assets/*.js / assets/*.css) that no longer exist
// on the server. Depending on how the host answers the missing file, the browser
// either fails the chunk request ("Failed to fetch dynamically imported module" /
// ChunkLoadError) or receives index.html and tries to parse HTML as a script
// ("Unexpected token '<'"). A single reload pulls the fresh index.html and the
// correct chunk names, so we reload once — guarded by a short cooldown so a
// genuinely-missing chunk can't put the tab into a reload loop.

const RELOAD_AT_KEY = "chunk-reload-at"
const RELOAD_COOLDOWN = 10000

const isChunkLoadError = (error: any): boolean => {
  const name = error?.name || ""
  const message = error?.message || ""

  return (
    name === "ChunkLoadError" ||
    /Loading( CSS)? chunk [^\s]+ failed/i.test(message) ||
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message) ||
    message.includes("Unexpected token '<'")
  )
}

// Reload the page at most once per cooldown window. Returns true if a reload was
// triggered, false if we reloaded too recently (i.e. the chunk is still missing
// after a fresh load — a real problem, let the caller handle it).
const reloadOnce = (): boolean => {
  let lastReload = 0

  try {
    lastReload = Number(window.sessionStorage.getItem(RELOAD_AT_KEY)) || 0
  } catch {
    lastReload = 0
  }

  if (Date.now() - lastReload < RELOAD_COOLDOWN) return false

  try {
    window.sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()))
  } catch {
    // sessionStorage unavailable (private mode / quota) — still reload once.
  }

  window.location.reload()
  return true
}

// Drop-in replacement for React.lazy that takes the same import factory.
const lazyRetry = <T extends ComponentType<any>>(importFn: () => Promise<{ default: T }>) =>
  lazy(() =>
    importFn().catch((error) => {
      if (isChunkLoadError(error) && reloadOnce()) {
        // Keep the component suspended while the page reloads.
        return new Promise<{ default: T }>(() => {})
      }

      throw error
    }),
  )

export default lazyRetry
export { isChunkLoadError, reloadOnce }
